import i18next from "i18next";
import { promangToast } from "../../../../operationFeedback";
import { isCompleteDeliveryAddress } from "../../../../constants/countries";

function isCancelledAgreement(item) {
  return (
    item?.deleted === true ||
    item?.status === "cancelled" ||
    String(item?.status || "").toLowerCase() === "cancelled"
  );
}

function hasKnownEmptyOrderlines(item) {
  const lines = item?.orderlines;
  // List responses omit orderlines. Unknown is not empty; the create endpoint
  // remains authoritative and rejects agreements that actually have no lines.
  if (!Array.isArray(lines)) return false;
  return !lines.some((line) => line && !line._destroy);
}

/**
 * Why a next-job CTA is blocked (for visible copy — not toast-only).
 * @returns {'no_customer'|'invalid_address'|'active_report'|'no_orderlines'|null}
 */
export function getServiceAgreementNextJobDisabledReason(item, { forOrder } = {}) {
  const hasCustomer = Boolean(item?.customer?.id);
  const addressOk = isCompleteDeliveryAddress(item?.delivery_address);
  const hasActive = Boolean(item?.active_report?.id);

  if (item?.follow_up_enabled === false) return "not_active";
  if (!hasCustomer) return "no_customer";
  if (!addressOk) return "invalid_address";
  if (forOrder && hasKnownEmptyOrderlines(item)) return "no_orderlines";
  if (forOrder && hasActive) return "active_report";
  return null;
}

export function nextJobDisabledReasonMessage(reason, { forOrder } = {}) {
  if (!reason) return null;
  const keys = {
    not_active: "agreementWorkflow.noFollowUp",
    no_customer: forOrder
      ? "service.agreements.actions.err_no_customer_order"
      : "service.agreements.actions.err_no_customer_report",
    invalid_address: "service.agreements.actions.err_invalid_address",
    active_report: forOrder
      ? "service.agreements.actions.err_active_report_order"
      : "service.agreements.actions.err_active_report_report",
    no_orderlines: "service.agreements.actions.err_no_orderlines",
  };
  const key = keys[reason];
  return key ? i18next.t(key) : null;
}

/**
 * Same branching as ServiceAgreementNewReportSection (agreement detail sidebar).
 * @returns {{ kind: string, disabled?: boolean, disabledReason?: string|null, report?: object }}
 */
export function getServiceAgreementNextJobUi(item) {
  if (isCancelledAgreement(item)) {
    return {
      kind: "cancelled",
      disabled: true,
      disabledReason: "cancelled",
    };
  }
  if (!item?.type || item.type.mission === undefined) {
    return { kind: "missing_type", disabled: true, disabledReason: "missing_type" };
  }
  const hasActive = Boolean(item?.active_report?.id);

  if (item.type.mission === true) {
    if (hasActive && item?.follow_up_enabled !== false) {
      return { kind: "link_report", report: item.active_report };
    }
    const disabledReason = getServiceAgreementNextJobDisabledReason(item, {
      forOrder: false,
    });
    return {
      kind: "create_report",
      disabled: Boolean(disabledReason),
      disabledReason,
    };
  }

  const disabledReason = getServiceAgreementNextJobDisabledReason(item, {
    forOrder: true,
  });
  return {
    kind: "create_order",
    disabled: Boolean(disabledReason),
    disabledReason,
  };
}

export function validateServiceAgreementCreateReport(item) {
  if (!item?.id) {
    promangToast.error(i18next.t("service.agreements.actions.err_no_agreement"));
    return false;
  }
  if (isCancelledAgreement(item)) {
    promangToast.error(i18next.t("service.agreements.actions.err_cancelled"));
    return false;
  }
  if (item?.follow_up_enabled === false) {
    promangToast.error(i18next.t("agreementWorkflow.noFollowUp"));
    return false;
  }
  if (item?.active_report?.id) {
    promangToast.error(i18next.t("service.agreements.actions.err_active_report_report"));
    return false;
  }
  if (!item?.customer?.id) {
    promangToast.error(i18next.t("service.agreements.actions.err_no_customer_report"));
    return false;
  }
  if (!isCompleteDeliveryAddress(item?.delivery_address)) {
    promangToast.error(i18next.t("service.agreements.actions.err_invalid_address"));
    return false;
  }
  return true;
}

export function validateServiceAgreementCreateOrder(item) {
  if (!item?.id) {
    promangToast.error(i18next.t("service.agreements.actions.err_no_agreement"));
    return false;
  }
  if (isCancelledAgreement(item)) {
    promangToast.error(i18next.t("service.agreements.actions.err_cancelled"));
    return false;
  }
  if (item?.follow_up_enabled === false) {
    promangToast.error(i18next.t("agreementWorkflow.noFollowUp"));
    return false;
  }
  if (item?.active_report?.id) {
    promangToast.error(i18next.t("service.agreements.actions.err_active_report_order"));
    return false;
  }
  if (!item?.customer?.id) {
    promangToast.error(i18next.t("service.agreements.actions.err_no_customer_order"));
    return false;
  }
  if (!isCompleteDeliveryAddress(item?.delivery_address)) {
    promangToast.error(i18next.t("service.agreements.actions.err_invalid_address"));
    return false;
  }
  if (hasKnownEmptyOrderlines(item)) {
    promangToast.error(i18next.t("service.agreements.actions.err_no_orderlines"));
    return false;
  }
  return true;
}
